import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Input } from '@ordo/ui/input'
import { Separator } from '@ordo/ui/separator'
import { AsyncCombobox } from 'compositions/AsyncCombobox'
import { useContact } from 'hooks/api'
import { useAsyncLoader } from 'hooks/useAsyncLoader'
import { Plus, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { Contact } from 'types/contacts'

export interface EventVariable {
  key: string
  value: string
}

export interface TemplateVariableState {
  contactId: string | null
  eventVariables: EventVariable[]
}

export const DEFAULT_STATE: TemplateVariableState = {
  contactId: null,
  eventVariables: [{ key: 'date', value: new Date().toLocaleDateString() }],
}

/** Extract event.X references from a Liquid template body */
export function extractEventKeys(templateBody: string): string[] {
  const keys = new Set<string>()
  const regex = /\bevent\.(\w+)/g
  let match: RegExpExecArray | null
  while ((match = regex.exec(templateBody)) !== null) {
    keys.add(match[1])
  }
  return Array.from(keys)
}

function contactToRecord(contact: Contact): Record<string, string | null> {
  return {
    firstName: contact.firstName,
    lastName: contact.lastName,
    email: contact.email,
    phoneNumber: contact.phoneNumber,
    organizationName: contact.organizationName,
    notes: contact.notes,
    ordoUserId: contact.ordoUserId,
    timezone: contact.timezone,
  }
}

/** Build the nested variable record for Liquid rendering */
export function buildVariablesRecord(state: TemplateVariableState, contact: Contact | null): Record<string, unknown> {
  // Placeholder URLs so imported CIO templates that reference {{ unsubscribe_url }}
  // render visibly in the preview. Real sends use signed per-recipient JWTs.
  const result: Record<string, unknown> = {
    unsubscribe_url: `/unsub/preview`,
    manage_preferences_url: `/unsub/preview/manage`,
  }

  if (state.contactId && contact?.id === state.contactId) {
    result.contact = contactToRecord(contact)
  }

  const event: Record<string, string> = {}
  for (const v of state.eventVariables) {
    const key = v.key.trim()
    if (key) {
      event[key] = v.value
    }
  }
  if (Object.keys(event).length > 0) {
    result.event = event
  }

  return result
}

function contactDisplayName(contact: Contact): string {
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ')
  if (name && contact.organizationName) {
    return `${name} (${contact.organizationName})`
  }
  return name || contact.email || '(unnamed)'
}

export function TemplateVariables({
  state,
  onChange,
  templateBody,
}: {
  state: TemplateVariableState
  onChange: (state: TemplateVariableState) => void
  templateBody?: string
}) {
  const loadContacts = useAsyncLoader<Contact>('/api/contacts', { active: true })
  const { data: selectedContact } = useContact(state.contactId ?? undefined)
  const hasInferredRef = useRef(false)

  // Infer event keys from template body on initial load only
  useEffect(() => {
    if (!templateBody || hasInferredRef.current) {
      return
    }
    hasInferredRef.current = true
    const inferred = extractEventKeys(templateBody)
    const existingKeys = new Set(state.eventVariables.map((v) => v.key.trim()))
    const newKeys = inferred.filter((k) => !existingKeys.has(k))

    if (newKeys.length > 0) {
      onChange({
        ...state,
        eventVariables: [...state.eventVariables, ...newKeys.map((key) => ({ key, value: '' }))],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateBody])

  const setContact = useCallback(
    (c: Contact | null) => {
      onChange({ ...state, contactId: c?.id ?? null })
    },
    [state, onChange],
  )

  const updateEventVar = useCallback(
    (index: number, field: 'key' | 'value', val: string) => {
      const next = state.eventVariables.map((v, i) => (i === index ? { ...v, [field]: val } : v))
      onChange({ ...state, eventVariables: next })
    },
    [state, onChange],
  )

  const addEventVar = useCallback(() => {
    onChange({ ...state, eventVariables: [...state.eventVariables, { key: '', value: '' }] })
  }, [state, onChange])

  const removeEventVar = useCallback(
    (index: number) => {
      onChange({ ...state, eventVariables: state.eventVariables.filter((_, i) => i !== index) })
    },
    [state, onChange],
  )

  const selectedValue = useMemo<Contact | null>(
    () => (state.contactId && selectedContact?.id === state.contactId ? selectedContact : null),
    [state.contactId, selectedContact],
  )

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium text-muted-foreground">Preview Variables</CardTitle>
      </CardHeader>
      <Separator />
      <CardContent className="space-y-4 pt-4">
        {/* Contact section */}
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Contact</div>
          <p className="text-xs text-muted-foreground">
            Available as <code className="rounded bg-muted px-1 font-mono">contact.*</code> in the template.
          </p>
          <AsyncCombobox<Contact>
            value={selectedValue}
            onChange={setContact}
            load={loadContacts}
            getKey={(c) => c.id}
            getLabel={contactDisplayName}
            getSublabel={(c) => c.email ?? c.phoneNumber ?? ''}
            placeholder="Select a contact…"
            searchPlaceholder="Search contacts…"
            emptyMessage="No contacts found"
            clearable
            clearLabel="No contact selected"
            className="h-8 text-sm"
          />
        </div>

        <Separator />

        {/* Event section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Event</div>
            <Button variant="ghost" size="sm" onClick={addEventVar} className="h-7 px-2 text-xs text-muted-foreground">
              <Plus className="mr-1 h-3.5 w-3.5" />
              Add
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Available as <code className="rounded bg-muted px-1 font-mono">event.*</code> in the template.
          </p>
          {state.eventVariables.length === 0 ? (
            <p className="text-xs text-muted-foreground">No event variables. Click Add to create one.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {state.eventVariables.map((variable, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={variable.key}
                    onChange={(e) => updateEventVar(index, 'key', e.target.value)}
                    placeholder="key"
                    className="h-8 min-w-0 flex-1 font-mono text-xs"
                  />
                  <Input
                    value={variable.value}
                    onChange={(e) => updateEventVar(index, 'value', e.target.value)}
                    placeholder="value"
                    className="h-8 min-w-0 flex-2 text-xs"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeEventVar(index)}
                    className="h-8 w-8 shrink-0 p-0 text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Separator />

        {/* Unsubscribe URL section */}
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Unsubscribe</div>
          <p className="text-xs text-muted-foreground">
            Injected automatically at send time. The preview shows a placeholder link.
          </p>
          <ul className="space-y-1 text-xs text-muted-foreground">
            <li>
              <code className="rounded bg-muted px-1 font-mono">unsubscribe_url</code> — one-click unsubscribe link.
              Signed per recipient at send time.
            </li>
            <li>
              <code className="rounded bg-muted px-1 font-mono">manage_preferences_url</code> — topic preferences page.
              Signed per recipient at send time.
            </li>
          </ul>
        </div>
      </CardContent>
    </Card>
  )
}
